import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useSelector, { useDispatch } from 'components/Booking-new/hooks';
import { setIsError, setTime } from 'components/Booking-new/actions';
import { pushGTMAppointmentTimeSelectedEvent, pushGTMCalendarDisplayedEvent, types } from 'utils/gtm';
import { getCurrentDate, getDaysBetween } from 'utils/date';
import NewsBanner, { BannerWrapper, shouldDisplayNewsBannerInBooking } from 'components/common/NewsBanner';
import stepTypes from 'components/Booking-new/modules/Steps/types';
import Loader from 'components/Booking-new/modules/Steps/shared/Loader';
import { getNumberOfClosestLocations } from 'utils/getClosestLocations';
import { getServiceCode, getServiceData } from 'components/Booking-new/utils/steps/service';
import { getTransformedSlotsData, getTimeSlots } from 'components/Booking-new/utils/steps/datetime';

const TimeContainer = (Component, { data, navigation }) => {
  const dispatch = useDispatch();
  const { goToNextStep, goToStepType } = navigation;
  const updateState = time => dispatch(setTime(time));

  const { results } = useSelector(state => ({
    isError: state?.isError,
    userData: state?.userData,
    serviceCode: getServiceCode(state?.userData[stepTypes.reschedule]?.service.service, state?.config?.service?.code),
    serviceData:
      getServiceData(state?.services.nodes, state?.userData[stepTypes.reschedule]?.service.service) ||
      state?.config?.service,
    freeParking: state?.config?.freeParking,
    isStepLocationDisabled: state?.allSteps?.find(step => step.type === stepTypes.location)?.isDisabled,
    rescheduleData: state?.userData[stepTypes.reschedule]?.appointment?.appointmentid,
    showNewsBanner: state?.config?.showNewsBanner,
    waitlistFlow: state?.config?.waitlistFlow,
    closestLocations: getNumberOfClosestLocations(
      state.locations,
      state?.userData[stepTypes.location]?.selectedLocation,
      3,
    ),
    distancePlaceholder: state?.allSteps?.find(step => step.type === stepTypes.location)?.data?.distancePlaceholder,
    funnelType: state?.config?.funnelType,
  }));

  const isRescheduling = !!results?.rescheduleData;
  const type = results?.rescheduleData ? types.reschedule : types.book;
  const selectedTime = results?.userData?.[stepTypes.time]?.time;
  const selectedLocation = results?.userData?.[stepTypes.location]?.selectedLocation;

  const [timeSlotResponse, setTimeSlotResponse] = useState(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        const timeSlots = await getTimeSlots(
          selectedLocation?.code,
          results?.serviceData?.code,
          data.maxDaysToShow,
          isRescheduling,
        );

        if (timeSlots?.error?.noDatesAvailable) return setIsLoadingSlots(false);
        if (timeSlots?.error) dispatch(setIsError(true));
        else if (timeSlots?.data) setTimeSlotResponse(timeSlots.data);

        return setIsLoadingSlots(false);
      } catch (e) {
        console.log('Time Slots fetching error', e);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [selectedLocation, results]);

  useEffect(() => {
    if (isLoadingSlots || !timeSlotResponse?.slots) return;

    const { totalAmountOfSlots, days, extraDays, newSlots } = getTransformedSlotsData(timeSlotResponse?.slots);

    const eventBody = {
      location: selectedLocation.code,
      type,
      slots: timeSlotResponse?.slots,
      totalAmountOfSlots,
      days,
      extraDays,
      newSlots,
      service: results?.serviceCode,
      funnelType: results?.funnelType,
    };

    pushGTMCalendarDisplayedEvent(eventBody);
  }, [isLoadingSlots]);

  const handleTimeSelect = timestamp => {
    if (typeof window === 'undefined') return goToNextStep?.();

    // trigger GTM event "Appointment Time selected"
    const [sdate, stime] = timestamp.split('T');
    const currentDate = getCurrentDate();
    const daysBetween = getDaysBetween(currentDate, sdate);

    const eventBody = {
      location: selectedLocation?.code,
      hourOfDay: stime,
      selectedDate: sdate,
      currentDate,
      daysBetween: `${daysBetween}`,
      type,
      service: results?.serviceCode,
      funnelType: results?.funnelType,
    };

    pushGTMAppointmentTimeSelectedEvent(eventBody);
    updateState(timestamp);
    goToNextStep?.();
  };

  const stepTimeComponentData = {
    stepTimeCF: data,
    timeSlotResponse,
    handleTimeSelect,
    selectedTime,
    selectedLocation,
    service: results?.serviceData,
    freeParking: results?.freeParking,
    waitlistFlow: results?.waitlistFlow,
    personalData: results?.userData?.[stepTypes.personalData],
    isBackToLocationAvailable: !results?.isStepLocationDisabled,
    goToStepType,
    closestLocations: results.closestLocations,
    distancePlaceholder: results.distancePlaceholder,
    setIsLoadingSlots,
    funnelType: results?.funnelType,
  };

  const newsBannerVisible = shouldDisplayNewsBannerInBooking(data?.newsBanner, results?.showNewsBanner);

  return (
    <div>
      {newsBannerVisible && !isLoadingSlots && (
        <BannerWrapper>
          <NewsBanner newsBanner={data?.newsBanner} overWriteBackColor={true} />
        </BannerWrapper>
      )}
      {isLoadingSlots ? <Loader /> : !results?.isError && <Component {...stepTimeComponentData} />}
    </div>
  );
};

TimeContainer.propTypes = {
  data: PropTypes.shape({
    newsBanner: PropTypes.object,
    maxDaysToShow: PropTypes.number,
  }),
  navigation: PropTypes.shape({
    goToNextStep: PropTypes.func,
    goToStepType: PropTypes.func,
  }),
};

export default TimeContainer;
